import { Row } from '@components/Layout/Row';
import { createResource, ResourceDataType, ResourceErrorsType } from '@components/SummarySelection/data/createResource';
import { SummaryBar } from '@components/SummarySelection/SummaryBar';
import { SummaryPackages } from '@components/SummarySelection/SummaryPackages';
import { getSubdomainData } from '@lib/utils';
import Divider from '@material-ui/core/Divider';
import Slide from '@material-ui/core/Slide';
import { dispatchWindowEvent } from '@packages/events/lib/dispatchEvent';
import { FooterMountPoint } from '@packages/layout/lib/UIShell/FooterMountPoint';
import { useLoading } from '@packages/loading';
import { MountTo } from '@packages/portals';
import { delay } from '@packages/promises/lib/delay';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { SummaryContext } from './context';
import { SummarySkus } from './SummarySkus';

export const SummaryData = createMemo<{
    showPrices?: boolean;
    hideSizingKitItem?: boolean;
    category: any;
    isDR?: boolean;
}>(
    ({ showPrices, hideSizingKitItem, category, isDR }) => {
        const [state, actions] = useContext(SummaryContext);
        const category_id = category;
        const customer_id = state.get('customer_id');
        const requestData = useLoading();
        const data = useRef<ResourceDataType>({ categories: null, packages: null, skus: null });
        const errors = useRef<ResourceErrorsType>({ categories: null, packages: null, skus: null });
        const wholesalerBySubdomain = getSubdomainData();
        const wholesalerParam = { wholesaler_id: wholesalerBySubdomain?.id };

        useEffect(() => {
            const dataFetcher = Promise.all([
                !(state.get('withProducts') || state.get('withSkus'))
                    ? (delay(0) as any)
                    : createResource(
                          'categories',
                          data,
                          errors,
                          { ...wholesalerParam },
                          state.get('categoryPrepareConfig'),
                          state.get('categoryTransformData'),
                      ),

                !state.get('withProducts')
                    ? (delay(0) as any)
                    : createResource(
                          'packages',
                          data,
                          errors,
                          {
                              category_id,
                              customer_id,
                              published: 1,
                              ...wholesalerParam,
                          },
                          state.get('packagePrepareConfig'),
                          state.get('packageTransformData'),
                      ),

                !state.get('withSkus')
                    ? (delay(0) as any)
                    : createResource(
                          'skus',
                          data,
                          errors,
                          {
                              category_id,
                              customer_id,
                              published: 1,
                              ...wholesalerParam,
                          },
                          state.get('skuPrepareConfig'),
                          state.get('skuTransformData'),
                      ),
            ]);

            dataFetcher.finally(() => {
                actions.setCategory({ category_id });
                actions.setData(data);
                actions.setErrors(errors);
            });

            requestData.fromPromise(dataFetcher);
        }, [category_id]);

        useEffect(() => {
            actions.setLoading(requestData.loading);
            const { skus }: any = data.current;
            if (skus?.items && hideSizingKitItem) {
                skus.items = skus?.items?.filter((item) => item.is_sizing_kit === false);
                data.current.skus = skus;
            }
        }, [requestData.loading]);

        const handleResize = useCallback(() => {
            dispatchWindowEvent('resize');
        }, ['']);

        const hasData = !!state.get('package_id') || (state.get('skus_ids') || []).length > 0;

        return (
            <>
                <Row layout={'column'} align={'start'} justify={'start'}>
                    {state.get('withProducts') ? <SummaryPackages showPrices={showPrices} isDR={isDR} /> : null}
                    {IF(
                        state.get('withProducts') && hasItems(data.current.skus) && hasItems(data.current.packages),
                        <Divider style={{ width: '100%', margin: '8px 0' }} />,
                    )}
                    {state.get('withSkus') ? <SummarySkus showPrices={showPrices} isDR={isDR} /> : null}
                </Row>

                {IF(
                    !state.get('noBar'),
                    <MountTo to={FooterMountPoint.POINT} name={'summary-bar'} order={5}>
                        <Slide
                            in={!requestData.loading && hasData}
                            direction={'up'}
                            onEntered={handleResize}
                            unmountOnExit
                        >
                            <SummaryBar />
                        </Slide>
                    </MountTo>,
                )}
            </>
        );
    },
    { displayName: 'SummaryData' },
);

const hasItems = (data: any): boolean => data && data.items && data.items.length > 0;
